/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  collection,
  onSnapshot,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from '../../services/firebase'
import { useWorkspace } from '../../context/useWorkspace'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'
import { isTimesheetsEnabled, isTimesheetsTierEligible } from '../../services/featureRegistry'
import { useMyClock } from '../../hooks/useMyClock'
import { showStoppedTimerUndoToast, type StoppedClockInfo } from './timesheetsToastHelper'
import type { ClockState } from '../../types/timesheets'

export interface ItemTimeContextValue {
  workspaceId: string
  isEnabled: boolean
  clock: ClockState | null
  isPending: boolean
  startTimer: (nodeId: string) => Promise<unknown>
  stopTimer: (nodeId: string) => Promise<unknown>
  busyNodeId: string | null
  minutesByNodeId: Record<string, number>
  registerNode: (nodeId: string) => () => void
  now: number
}

export const ItemTimeContext = createContext<ItemTimeContextValue | null>(null)

export interface ItemTimeProviderProps {
  workspaceId?: string | null
  children: ReactNode
}

export const ItemTimeProvider = ({ workspaceId, children }: ItemTimeProviderProps) => {
  const { activeWorkspaceId, workspaces = [], activeRole } = useWorkspace()
  const { user } = useAuth()
  const { showToast } = useToast()
  const { t } = useNls()

  const targetWsId = workspaceId || activeWorkspaceId || ''
  const workspace = (workspaces ?? []).find((w) => w.id === targetWsId) ?? null

  const isMember = Boolean(workspace && (user || activeRole))
  const isEnabled = useMemo(() => {
    if (!workspace || !isMember) return false
    return (
      isTimesheetsEnabled(workspace) &&
      isTimesheetsTierEligible(workspace.plan, workspace)
    )
  }, [workspace, isMember])

  // Single useMyClock for the entire surface
  const myClock = useMyClock({
    workspaceId: isEnabled ? targetWsId : '',
    uid: user?.uid,
  })
  const { clock, isPending, startTimer: clockStartTimer, stopTimer: clockStopTimer } = myClock

  // Single 1s ticker only while an active TIMER is running
  const [now, setNow] = useState<number>(() => Date.now())
  const isTimerRunning = Boolean(
    clock && clock.kind === 'TIMER' && clock.nodeId && clock.startedAt,
  )

  useEffect(() => {
    if (!isTimerRunning) return
    const interval = setInterval(() => {
      setNow(Date.now())
    }, 1000)
    return () => clearInterval(interval)
  }, [isTimerRunning])

  // Node ID registry
  const registryRef = useRef<Map<string, number>>(new Map())
  const [activeNodeIds, setActiveNodeIds] = useState<string[]>([])
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flushRegistry = useCallback(() => {
    const ids: string[] = []
    for (const [id, count] of registryRef.current.entries()) {
      if (count > 0 && id && !id.startsWith('draft-')) {
        ids.push(id)
      }
    }
    ids.sort()
    setActiveNodeIds((prev) => {
      if (prev.length === ids.length && prev.every((v, i) => v === ids[i])) {
        return prev
      }
      return ids
    })
  }, [])

  const registerNode = useCallback(
    (nodeId: string) => {
      if (!nodeId || nodeId.startsWith('draft-')) {
        return () => {}
      }
      const current = registryRef.current.get(nodeId) || 0
      registryRef.current.set(nodeId, current + 1)

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(flushRegistry, 16)

      return () => {
        const remaining = (registryRef.current.get(nodeId) || 1) - 1
        if (remaining <= 0) {
          registryRef.current.delete(nodeId)
        } else {
          registryRef.current.set(nodeId, remaining)
        }
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
        }
        debounceTimerRef.current = setTimeout(flushRegistry, 16)
      }
    },
    [flushRegistry],
  )

  // Chunked time_entries subscriptions (max 30 nodeIds per query)
  const [minutesByNodeId, setMinutesByNodeId] = useState<Record<string, number>>({})
  const chunkResultsRef = useRef<Map<number, Record<string, number>>>(new Map())

  useEffect(() => {
    if (!isEnabled || !targetWsId || !user?.uid || activeNodeIds.length === 0) {
      return
    }

    const unsubs: Unsubscribe[] = []
    chunkResultsRef.current.clear()

    const CHUNK_SIZE = 30
    const chunks: string[][] = []
    for (let i = 0; i < activeNodeIds.length; i += CHUNK_SIZE) {
      chunks.push(activeNodeIds.slice(i, i + CHUNK_SIZE))
    }

    const recomputeMinutes = () => {
      const merged: Record<string, number> = {}
      for (const chunkMap of chunkResultsRef.current.values()) {
        for (const [k, v] of Object.entries(chunkMap)) {
          merged[k] = (merged[k] || 0) + v
        }
      }
      setMinutesByNodeId(merged)
    }

    chunks.forEach((chunk, chunkIdx) => {
      try {
        const q = query(
          collection(firestoreDb, 'time_entries'),
          where('workspaceId', '==', targetWsId),
          where('uid', '==', user.uid),
          where('nodeId', 'in', chunk),
        )

        const unsub = onSnapshot(
          q,
          (snap) => {
            const chunkMap: Record<string, number> = {}
            snap.docs.forEach((d) => {
              const status = d.get('status')
              if (status === 'REJECTED') return
              const nId = d.get('nodeId')
              if (!nId) return
              const mins = Number(d.get('minutes')) || 0
              chunkMap[nId] = (chunkMap[nId] || 0) + mins
            })
            chunkResultsRef.current.set(chunkIdx, chunkMap)
            recomputeMinutes()
          },
          (err) => {
            console.error('[ItemTimeProvider] time_entries chunk query error:', err)
          },
        )

        unsubs.push(unsub)
      } catch (err) {
        console.error('[ItemTimeProvider] failed to create query:', err)
      }
    })

    const chunkResults = chunkResultsRef.current
    return () => {
      unsubs.forEach((u) => u())
      chunkResults.clear()
    }
  }, [isEnabled, targetWsId, user?.uid, activeNodeIds])

  const [busyNodeId, setBusyNodeId] = useState<string | null>(null)

  const startTimer = useCallback(
    async (nodeId: string) => {
      if (busyNodeId || !targetWsId || !nodeId) return
      setBusyNodeId(nodeId)
      try {
        const res = await clockStartTimer({ nodeId, device: 'web' })
        if (res && typeof res === 'object' && 'stoppedClock' in res && res.stoppedClock) {
          showStoppedTimerUndoToast({
            workspaceId: targetWsId,
            stoppedClock: res.stoppedClock as StoppedClockInfo,
            showToast,
            t,
          })
        }
      } catch (err) {
        const msg =
          err instanceof Error && err.message.trim().length > 0
            ? err.message
            : typeof err === 'object' && err && 'message' in err && String((err as { message: unknown }).message).trim().length > 0
              ? String((err as { message: unknown }).message)
              : t('NLSTimesheetsTimerActionFailed')
        showToast(msg, 'error')
      } finally {
        setBusyNodeId(null)
      }
    },
    [busyNodeId, targetWsId, clockStartTimer, showToast, t],
  )

  const stopTimer = useCallback(
    async (nodeId: string) => {
      if (busyNodeId || !targetWsId || !nodeId) return
      setBusyNodeId(nodeId)
      try {
        await clockStopTimer()
        showToast(t('NLSTimesheetsEntrySavedToast'), 'success')
      } catch (err) {
        const msg =
          err instanceof Error && err.message.trim().length > 0
            ? err.message
            : typeof err === 'object' && err && 'message' in err && String((err as { message: unknown }).message).trim().length > 0
              ? String((err as { message: unknown }).message)
              : t('NLSTimesheetsTimerActionFailed')
        showToast(msg, 'error')
      } finally {
        setBusyNodeId(null)
      }
    },
    [busyNodeId, targetWsId, clockStopTimer, showToast, t],
  )

  const value = useMemo(
    () => ({
      workspaceId: targetWsId,
      isEnabled,
      clock,
      isPending,
      startTimer,
      stopTimer,
      busyNodeId,
      minutesByNodeId,
      registerNode,
      now,
    }),
    [
      targetWsId,
      isEnabled,
      clock,
      isPending,
      startTimer,
      stopTimer,
      busyNodeId,
      minutesByNodeId,
      registerNode,
      now,
    ],
  )

  return <ItemTimeContext.Provider value={value}>{children}</ItemTimeContext.Provider>
}

export interface UseItemTimeResult {
  minutes: number
  isRunningHere: boolean
  elapsedText: string
  start: () => Promise<void>
  stop: () => Promise<void>
  busy: boolean
}

export function useItemTime(nodeId?: string | null): UseItemTimeResult {
  const context = useContext(ItemTimeContext)

  // Auto-register node with provider
  useEffect(() => {
    if (!context || !nodeId || nodeId.startsWith('draft-')) return
    return context.registerNode(nodeId)
  }, [context, nodeId])

  if (!context || !nodeId || !context.isEnabled) {
    return {
      minutes: 0,
      isRunningHere: false,
      elapsedText: '00:00',
      start: async () => {},
      stop: async () => {},
      busy: false,
    }
  }

  const { clock, now, minutesByNodeId, startTimer, stopTimer, busyNodeId } = context

  const isRunningHere = Boolean(
    clock && clock.kind === 'TIMER' && clock.nodeId === nodeId,
  )

  let elapsedMinutes = 0
  let elapsedText = '00:00'

  if (isRunningHere && clock?.startedAt) {
    const startMs = new Date(clock.startedAt).getTime()
    if (!isNaN(startMs)) {
      const diffMs = Math.max(0, now - startMs)
      elapsedMinutes = diffMs / 60000
      const totalSeconds = Math.floor(diffMs / 1000)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      elapsedText =
        hours > 0
          ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
          : `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
  }

  const recordedMinutes = minutesByNodeId[nodeId] || 0
  const minutes = recordedMinutes + elapsedMinutes
  const busy = busyNodeId === nodeId

  const start = async () => {
    await startTimer(nodeId)
  }

  const stop = async () => {
    await stopTimer(nodeId)
  }

  return {
    minutes,
    isRunningHere,
    elapsedText,
    start,
    stop,
    busy,
  }
}
